// ============================================================================
// DriverTimeline.jsx
// One driver's shifts drawn on a shared time axis, with the two Time and Wage
// Audit datasets laid over the same bar: meal-break gaps inside the shift, and
// the end-of-shift sequence (yard arrival -> inspection -> punch out) at its
// tail. Each segment is a button; clicking one opens its detail.
//
// Why a timeline rather than two tables: the datasets only become worth
// reading together when they share an axis. "A 32-minute gap at 23:42" and
// "back at the yard 19:11, out 19:24" are two rows in two tables until you can
// see they belong to the same fourteen-hour shift.
//
// ── Everything here is Pacific ───────────────────────────────────────────────
// Positions are computed as elapsed minutes from each shift's own start, then
// offset by that start's Pacific minute-of-day. That is deliberate rather than
// converting each instant independently: shifts routinely cross midnight (a
// punch-out at 00:03 UTC is 17:03 the previous Pacific day), and elapsed-time
// arithmetic from one anchor cannot produce a bar that runs backwards. It also
// means the axis needs no special case for the DST change.
//
// The underlying instants carry mixed offsets by origin — Workday punches and
// yard arrival as +00:00, Fleetio inspections as the real Pacific offset — so
// every value here goes through Date parsing rather than string slicing.
// ============================================================================

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { c, t, sp, r, card, chart } from '../../theme';
import { fmtNumber, fmtDate, fmtTimePT, splitWorkerName } from './format';
import SegmentPopover from './SegmentPopover';
import TimelineLegend from './TimelineLegend';
import { hhmm } from './timeline';

// ── Time helpers ─────────────────────────────────────────────────────────────

/** Minute-of-day (0–1439) of an instant, read in Pacific. */
function ptMinuteOfDay(iso) {
  if (!iso) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === 'hour')?.value);
  const m = Number(parts.find((p) => p.type === 'minute')?.value);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

/** Elapsed minutes between two instants — sign preserved. */
function minutesBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  return (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60000;
}

// hhmm is imported from timeline.js rather than kept as a second copy here.
// This file had its own, so switching the axis to 12-hour in the shared module
// would have left the drill-down reading 17:00 beside a list reading 5 PM —
// the same silent divergence the shared geometry module exists to prevent.

function fmtMin(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(1)} min`;
}

function fmtHours(mins) {
  if (mins === null || mins === undefined) return '—';
  return `${(Number(mins) / 60).toFixed(1)} h`;
}

// ── Geometry ─────────────────────────────────────────────────────────────────
// Each day resolves to positions on one axis measured in Pacific minutes.
function layoutDay(day) {
  const pt = day.post_trip;
  const anchorIso = pt?.shift_start || day.meals[0]?.gap_start;
  if (!anchorIso) return null;

  const base = ptMinuteOfDay(anchorIso);
  if (base === null) return null;
  const at = (iso) => {
    const d = minutesBetween(anchorIso, iso);
    return d === null ? null : base + d;
  };

  const start = pt?.shift_start ? base : null;
  const end   = at(pt?.shift_end);

  return {
    date: day.date,
    anchorIso,
    shiftStart: start,
    shiftEnd:   end,
    workedMinutes: pt?.worked_minutes ?? (start !== null && end !== null ? end - start : null),
    yardArrival:  at(pt?.yard_arrival),
    inspStart:    at(pt?.inspection_started),
    inspEnd:      at(pt?.inspection_submitted),
    // Pre-trip (047) — the same shift's front end.
    preInspStart:  at(pt?.pre_inspection_started),
    preInspEnd:    at(pt?.pre_inspection_submitted),
    yardDeparture: at(pt?.yard_departure),
    meals: day.meals.map((m) => ({
      ...m,
      from: at(m.gap_start),
      to:   at(m.gap_end),
    })).filter((m) => m.from !== null),
    post_trip: pt,
    raw: day,
  };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function DriverTimeline({ data, onClose }) {
  const [selected, setSelected] = useState(null);   // { kind, day, meal? }

  // Escape closes, and the board behind does not scroll while this is open.
  // Without the lock the list scrolls under the backdrop and the reader loses
  // the row they came from — the thing a drill-down should preserve.
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const laid = useMemo(
    () => (data?.days || []).map(layoutDay).filter(Boolean),
    [data]
  );

  // Axis bounds from the SHIFT itself — punches, yard arrival and meal gaps —
  // and deliberately not from the inspection timestamps.
  //
  // Inspection times are the one field here with known-bad outliers: a
  // form_left_open row carries a 1,027-minute "inspection", i.e. a form opened
  // and submitted seventeen hours apart. Including those in the bounds
  // stretches the axis to a full 24 hours and squashes every real shift into a
  // few pixels — one bad row silently destroying the readability of every good
  // one. Markers outside the resulting window are clamped to its edge and
  // flagged, rather than allowed to set the scale.
  const { axisFrom, axisTo } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const d of laid) {
      for (const v of [d.shiftStart, d.shiftEnd, d.yardArrival, d.yardDeparture,
                       ...d.meals.flatMap((m) => [m.from, m.to])]) {
        if (v === null || v === undefined || !Number.isFinite(v)) continue;
        lo = Math.min(lo, v); hi = Math.max(hi, v);
      }
    }
    if (!Number.isFinite(lo)) return { axisFrom: 5 * 60, axisTo: 20 * 60 };
    return { axisFrom: Math.floor(lo / 60) * 60 - 30, axisTo: Math.ceil(hi / 60) * 60 + 30 };
  }, [laid]);

  const span = Math.max(axisTo - axisFrom, 60);
  const pct  = (mins) => ((mins - axisFrom) / span) * 100;

  const ticks = useMemo(() => {
    const out = [];
    const step = span > 900 ? 180 : span > 480 ? 120 : 60;
    for (let m = Math.ceil(axisFrom / step) * step; m <= axisTo; m += step) out.push(m);
    return out;
  }, [axisFrom, axisTo, span]);

  const b = data?.benchmarks;
  const driver = data?.driver || {};

  // The driver's own figures, computed from the days on screen so the
  // comparison always describes exactly what the reader is looking at — a
  // server-side number over a different window would quietly disagree with
  // the bars above it.
  const own = useMemo(() => {
    const measured = laid.filter((d) => d.post_trip?.category === 'measured');
    const med = (arr) => (arr.length ? arr.slice().sort((x, y) => x - y)[Math.floor(arr.length / 2)] : null);
    return {
      shifts: measured.length,
      medUnaccounted: med(measured.map((d) => Number(d.post_trip.unaccounted_min)).filter(Number.isFinite)),
      medInspection:  med(measured.map((d) => Number(d.post_trip.inspection_minutes)).filter(Number.isFinite)),
    };
  }, [laid]);

  const closeOutMax = Math.max(
    own.medUnaccounted ?? 0, Number(b?.yard_median_unaccounted) || 0, Number(b?.yard_avg_unaccounted) || 0, 1
  );
  const inspMax = Math.max(own.medInspection ?? 0, Number(b?.yard_median_inspection) || 0, 1);

  // A dialog, not a block pushed in above the list. Rendered inline, opening a
  // driver shoved the whole board down the page: the reader lost the row they
  // clicked, and closing it returned them to a scroll position that no longer
  // matched what they were reading. The portal also lifts it clear of the
  // sticky axis, which otherwise painted over the panel at z-index 2.
  return createPortal(
    <div
      style={s.backdrop}
      role="presentation"
      // mousedown rather than click: a drag that STARTS inside the panel and
      // ends on the backdrop — selecting a timestamp, say — would otherwise
      // close the dialog and throw away the selection.
      onMouseDown={onClose}
    >
      <div style={s.wrap} role="dialog" aria-modal="true"
           aria-label={`Timeline for ${splitWorkerName(driver.worker_name, driver.employee_id).name || 'driver'}`}
           onMouseDown={(e) => e.stopPropagation()}>
      <style>{css}</style>

      {/* ── Identity ───────────────────────────────────────────────────── */}
      <div style={s.head}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: sp[3], flexWrap: 'wrap' }}>
          <span style={s.name}>{splitWorkerName(driver.worker_name, driver.employee_id).name || 'Driver'}</span>
          <span style={s.sub}>
            ID {driver.employee_id}
            {driver.route ? ` · route ${driver.route}` : ''}
            {driver.cost_center_code ? ` · ${driver.cost_center_code} ${driver.cost_center_name || ''}` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: sp[4] }}>
          {driver.supervisor && (
            <span style={s.sub}>supervisor {splitWorkerName(driver.supervisor).name}</span>
          )}
          {onClose && (
            <button className="twa-x" style={s.close} onClick={onClose} aria-label="Close timeline">
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {/* The same legend as the list. Shared rather than reproduced: the
          drill-down draws the identical marks, and two copies would drift the
          first time a hue changed. */}
      <TimelineLegend
        thresholdMin={data.threshold_min ?? 45}
        preThresholdMin={data.pre_threshold_min ?? 60}
      />

      {/* ── Axis ───────────────────────────────────────────────────────── */}
      <div style={s.body}>
        <div style={s.axisRow}>
          <div style={s.gutter} />
          <div style={{ position: 'relative', flex: 1, height: 18 }}>
            {ticks.map((m) => (
              <span key={m} style={{ ...s.tick, left: `${pct(m)}%` }}>{hhmm(m)}</span>
            ))}
          </div>
          <div style={s.noteCol} />
        </div>
        <div style={s.axisRule} />

        {/* ── One row per shift ────────────────────────────────────────── */}
        {laid.map((d) => {
          const hasShift = d.shiftStart !== null && d.shiftEnd !== null;
          const lapse    = d.post_trip?.unaccounted_min;
          const isMeasured = d.post_trip?.category === 'measured';
          const preLapse    = d.post_trip?.pre_unaccounted_min;
          const preMeasured = d.post_trip?.pre_category === 'measured';

          return (
            <div key={d.date} style={s.shiftRow}>
              <div style={s.gutter}>
                <div style={s.dayLabel}>{fmtDate(d.date)}</div>
                <div style={s.dayMeta}>
                  {d.post_trip ? fmtHours(d.workedMinutes) : `${d.meals.length} gap${d.meals.length === 1 ? '' : 's'}`}
                </div>
              </div>

              <div style={{ position: 'relative', flex: 1, minHeight: 44 }}>
                {/* Hour grid, behind everything. Rendered first so the marks
                    paint over it, and pointer-events:none so it can never
                    swallow a click meant for a segment — an invisible overlay
                    eating clicks is the classic way a decorative layer breaks a
                    page while looking fine. Same tick positions as the axis
                    labels above, from the same array, so a line can never drift
                    from the hour it claims. */}
                {ticks.map((m) => (
                  <span key={`g${m}`} aria-hidden="true"
                        style={{ ...s.gridLine, left: `${pct(m)}%` }} />
                ))}


                {/* worked span */}
                {hasShift && (
                  <div
                    style={{
                      ...s.bar,
                      left:  `${pct(d.shiftStart)}%`,
                      width: `${Math.max(pct(d.shiftEnd) - pct(d.shiftStart), 0.6)}%`,
                    }}
                  >
                    <span style={s.barLabel}>{fmtHours(d.workedMinutes)} worked</span>
                  </div>
                )}

                {/* meal gaps — clickable */}
                {d.meals.map((m) => {
                  const left = pct(m.from);
                  const w    = m.to !== null ? Math.max(pct(m.to) - left, 0.5) : 0.5;
                  const clear = Number(m.pct_moving) >= 90;
                  return (
                    <button
                      key={m.id}
                      className="twa-seg"
                      title={`Meal gap ${fmtTimePT(m.gap_start)} — ${m.gap_minutes} min`}
                      onClick={(e) => setSelected({ kind: 'meal', row: data.driver, geo: d, meal: m,
                        anchorRect: e.currentTarget.getBoundingClientRect() })}
                      style={{
                        ...s.meal,
                        left: `${left}%`,
                        width: `${w}%`,
                        // Solid only for the tier the source system calls
                        // unambiguous; ambiguous gaps are outlined instead, so
                        // the two are separable at a glance without the page
                        // giving them equal visual weight.
                        background: clear ? c.danger : c.warningTint,
                        border: clear ? 'none' : `1px solid ${c.warning}`,
                      }}
                    />
                  );
                })}

                {/* start-of-shift sequence — clickable, mirror of the
                    close-out below. Only where a departure was detected. */}
                {d.post_trip && d.shiftStart !== null && d.yardDeparture !== null && (
                  <button
                    className="twa-seg"
                    title={`In ${fmtTimePT(d.post_trip.shift_start)} · left ${fmtTimePT(d.post_trip.yard_departure)}`}
                    onClick={(e) => setSelected({ kind: 'startshift', row: data.driver, geo: d,
                      anchorRect: e.currentTarget.getBoundingClientRect() })}
                    style={{
                      ...s.startshift,
                      left:  `${pct(d.shiftStart)}%`,
                      width: `${Math.max(pct(d.yardDeparture) - pct(d.shiftStart), 0.8)}%`,
                      // Same restraint as the close-out block: this sits on
                      // top of the green worked bar.
                      borderColor: preMeasured && preLapse > (data.pre_threshold_min ?? 60)
                        ? c.danger : chart.shiftStart,
                      background:  preMeasured && preLapse > (data.pre_threshold_min ?? 60)
                        ? chart.overFill : chart.shiftStartFill,
                    }}
                  />
                )}

                {d.preInspEnd !== null && (
                  <span
                    style={{
                      ...s.preInspTick,
                      left: `${Math.max(0, Math.min(pct(d.preInspEnd), 100))}%`,
                      opacity: d.preInspEnd < axisFrom || d.preInspEnd > axisTo ? 0.35 : 1,
                    }}
                    title={`Pre-trip submitted ${fmtTimePT(d.post_trip?.pre_inspection_submitted)}`}
                  />
                )}

                {/* end-of-shift sequence — clickable */}
                {d.post_trip && d.yardArrival !== null && d.shiftEnd !== null && (
                  <button
                    className="twa-seg"
                    title={`Back ${fmtTimePT(d.post_trip.yard_arrival)} · out ${fmtTimePT(d.post_trip.shift_end)}`}
                    onClick={(e) => setSelected({ kind: 'endshift', row: data.driver, geo: d,
                      anchorRect: e.currentTarget.getBoundingClientRect() })}
                    style={{
                      ...s.endshift,
                      left:  `${pct(d.yardArrival)}%`,
                      width: `${Math.max(pct(d.shiftEnd) - pct(d.yardArrival), 0.8)}%`,
                      // Kept light: this block sits ON TOP of the green worked
                      // bar, and a heavier tint muddies to beige and reads as
                      // a disabled region rather than a highlighted one.
                      borderColor: isMeasured && lapse > (data.threshold_min ?? 45)
                        ? c.danger : chart.shiftEnd,
                      background:  isMeasured && lapse > (data.threshold_min ?? 45)
                        ? chart.overFill : chart.shiftEndFill,
                    }}
                  />
                )}

                {/* Inspection submitted. Clamped into the window rather than
                    allowed off-canvas — an outlier should still be locatable. */}
                {d.inspEnd !== null && (
                  <span
                    style={{
                      ...s.inspTick,
                      left: `${Math.max(0, Math.min(pct(d.inspEnd), 100))}%`,
                      opacity: d.inspEnd < axisFrom || d.inspEnd > axisTo ? 0.35 : 1,
                    }}
                    title={`Inspection submitted ${fmtTimePT(d.post_trip?.inspection_submitted)}`}
                  />
                )}
              </div>

              {/* Annotation gets its own column. Absolutely positioning it
                  after the bar ran it off the right edge on any late shift. */}
              <div style={s.noteCol}>
                {d.post_trip
                  ? (isMeasured
                      ? <>
                          <span style={s.noteStrong}>{fmtMin(d.post_trip.unaccounted_min)}</span>
                          <span style={s.noteDim}>
                            back {fmtTimePT(d.post_trip.yard_arrival)} · out {fmtTimePT(d.post_trip.shift_end)}
                          </span>
                        </>
                      : <span style={s.noteDim}>{d.post_trip.category === 'out_before_return'
                          ? 'out before return'
                          : d.post_trip.completeness.replace(/_/g, ' ')}</span>)
                  : <span style={s.noteDim}>no post-trip record</span>}
              </div>
            </div>
          );
        })}

        {!laid.length && <div style={s.empty}>No shifts for this driver in the selected range.</div>}
      </div>

      {/* ── Detail for the clicked segment ─────────────────────────────── */}
      {/* Same popover the shift list uses, so a segment click behaves
          identically in both places. */}
      {selected && (
        <SegmentPopover
          selected={selected}
          thresholdMin={data.threshold_min ?? 45}
          preThresholdMin={data.pre_threshold_min ?? 60}
          onClose={() => setSelected(null)}
        />
      )}

      {/* ── This driver against the same yard ──────────────────────────── */}
      {b && Number(b.measured_shifts) > 0 && own.shifts > 0 && (
        <div style={s.bench}>
          <div style={s.benchTitle}>
            Against the same yard — this driver&rsquo;s {fmtNumber(own.shifts)} measured shift{own.shifts === 1 ? '' : 's'}
            {' '}vs {fmtNumber(b.measured_shifts)} at {b.yard_name}
          </div>

          {/* Both close-out rows share one scale, so the bars are comparable.
              Giving each row its own max made every bar full-width and the
              comparison meaningless. */}
          <BenchRow
            label="This driver, median" value={own.medUnaccounted}
            max={closeOutMax} tone={toneVs(own.medUnaccounted, Number(b.yard_median_unaccounted), 'high')}
          />
          <BenchRow label="Yard median" value={Number(b.yard_median_unaccounted)} max={closeOutMax} tone={c.borderStrong} />
          <BenchRow label="Yard average" value={Number(b.yard_avg_unaccounted)} max={closeOutMax} tone={c.borderStrong} />

          <div style={s.benchGap} />

          <BenchRow
            label="Inspection, this driver" value={own.medInspection}
            max={inspMax} tone={toneVs(own.medInspection, Number(b.yard_median_inspection), 'low')}
          />
          <BenchRow label="Inspection, yard median" value={Number(b.yard_median_inspection)} max={inspMax} tone={c.borderStrong} />

          <div style={s.benchNote}>
            Read against the median, not the average — a handful of very long close-outs pull
            the mean well above a normal shift. A <em>shorter</em> inspection than the yard
            typically runs is the direction worth a second look, not a longer one.
          </div>
        </div>
      )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Colour a driver's bar only when it is MATERIALLY different from the yard.
 *
 * The first version coloured on a plain > comparison, which painted a
 * 7.1-vs-6.7-minute gap bright red — a 24-second difference presented with the
 * same weight as a real outlier. On a page about named employees that is not a
 * cosmetic problem: the colour is the reader's first impression and it should
 * not manufacture a finding out of noise. Neutral unless the driver is at
 * least half again the yard figure and clear of the small-numbers floor.
 */
function toneVs(value, reference, badDirection) {
  if (!Number.isFinite(value) || !Number.isFinite(reference) || reference <= 0) return c.borderStrong;
  const ratio = value / reference;
  const absGap = Math.abs(value - reference);
  if (absGap < 3) return c.borderStrong;                    // minutes apart is not a signal
  if (badDirection === 'high' && ratio >= 1.5) return c.danger;
  if (badDirection === 'low'  && ratio <= 0.5) return c.warning;
  return c.borderStrong;
}

function BenchRow({ label, value, max, tone, unit = 'min' }) {
  const ok = Number.isFinite(value);
  const w = ok ? Math.max(Math.min((value / max) * 100, 100), 1.5) : 0;
  return (
    <div style={s.benchRow}>
      <div style={s.benchLabel}>{label}</div>
      <div style={s.benchTrack}>
        <div style={{ ...s.benchFill, width: `${w}%`, background: tone }} />
      </div>
      <div style={s.benchVal}>{ok ? `${value.toFixed(1)} ${unit}` : '—'}</div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
// Hover/focus can't be expressed inline, so the interactive bits go through a
// <style> block — the pattern used by Login/AppShell/PageHeader.
const css = `
  .twa-seg { cursor: pointer; transition: filter .12s ease, transform .12s ease; }
  .twa-seg:hover { filter: brightness(1.12); transform: scaleY(1.18); }
  .twa-seg:focus-visible { outline: 2px solid ${c.info}; outline-offset: 2px; }
  .twa-x { cursor: pointer; }
  .twa-x:hover { background: ${c.canvas}; }
  @media (prefers-reduced-motion: reduce) { .twa-seg { transition: none; } }
`;

const s = {
  // Above SegmentPopover's z-index 60 would trap the popover behind the panel,
  // so the backdrop sits below it and the panel below that: the popover is
  // opened FROM this dialog and has to render over it.
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.45)', zIndex: 40,
    // Centred. Safe to centre only because the panel caps itself at 92vh and
    // scrolls internally — a flex-centred child taller than its container has
    // its top clipped and unreachable, which is why the first version pinned
    // to flex-start instead.
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: `${sp[4]} ${sp[4]}`,
  },
  wrap: { ...card.base, background: c.surface, border: `1px solid ${c.border}`, overflow: 'hidden',
          width: 'min(1180px, 96vw)', maxHeight: '92vh', overflowY: 'auto', zIndex: 41,
          boxShadow: '0 24px 64px rgba(17, 24, 39, 0.28)' },

  head: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: sp[4],
    padding: `${sp[3]} ${sp[5]}`, borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk,
    flexWrap: 'wrap',
  },
  name: { ...t.h2, color: c.text },
  sub:  { ...t.bodySm, color: c.textMuted },
  close: {
    ...btnReset(), display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: r.sm, border: `1px solid ${c.border}`,
    background: c.surface, color: c.textMuted,
  },

  body: { padding: `${sp[4]} ${sp[5]} ${sp[2]}` },

  axisRow:  { display: 'flex', alignItems: 'flex-end', gap: sp[3] },
  gutter:   { width: 108, flexShrink: 0 },
  tick:     { position: 'absolute', transform: 'translateX(-50%)', ...t.micro, color: c.textFaint, ...t.tabular },
  axisRule: { height: 1, background: c.border, margin: `4px 0 ${sp[3]} 120px` },

  shiftRow: { display: 'flex', alignItems: 'center', gap: sp[3], marginBottom: sp[3] },
  dayLabel: { ...t.bodySm, fontWeight: 600, color: c.textStrong, ...t.tabular },
  dayMeta:  { ...t.micro, color: c.textFaint, ...t.tabular },

  bar: {
    position: 'absolute', top: 10, height: 24, borderRadius: r.sm,
    background: c.successTint, border: `1px solid ${c.success}33`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  barLabel: { ...t.micro, color: c.successText, whiteSpace: 'nowrap', ...t.tabular },

  // Sits INSIDE the worked bar rather than straddling it — a meal gap is time
  // within the shift, and drawing it taller than its container said otherwise.
  meal: {
    ...btnReset(), position: 'absolute', top: 13, height: 18, borderRadius: 2, minWidth: 3,
  },
  endshift: {
    ...btnReset(), position: 'absolute', top: 4, height: 36, borderRadius: r.sm,
    borderWidth: 1.5, borderStyle: 'solid', minWidth: 4,
  },
  gridLine: {
    position: 'absolute', top: 0, bottom: 0, width: 1, background: chart.grid,
    pointerEvents: 'none',
  },
  inspTick: {
    position: 'absolute', top: 2, height: 40, width: 2, background: chart.shiftEnd, borderRadius: 1,
    pointerEvents: 'none',
  },
  // Pre-trip mirrors, in the start hue: the two ends of the shift differ by
  // hue as well as by where they sit on the axis.
  startshift: {
    ...btnReset(), position: 'absolute', top: 4, height: 36, borderRadius: r.sm,
    borderWidth: 1.5, borderStyle: 'solid', minWidth: 4,
  },
  preInspTick: {
    position: 'absolute', top: 2, height: 40, width: 2, background: chart.shiftStart, borderRadius: 1,
    pointerEvents: 'none',
  },

  noteCol: {
    width: 190, flexShrink: 0, display: 'flex', flexDirection: 'column',
    gap: 1, paddingLeft: sp[2],
  },
  noteStrong: { ...t.bodySm, fontWeight: 650, color: c.text, ...t.tabular },
  noteDim:    { ...t.micro, color: c.textFaint, ...t.tabular },

  empty: { ...t.bodySm, color: c.textMuted, padding: `${sp[6]} 0`, textAlign: 'center' },

  detail: {
    margin: `0 ${sp[5]} ${sp[4]}`, padding: sp[4],
    background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: r.md,
  },
  detailHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: sp[3] },
  detailTitle: { ...t.h3, color: c.text, display: 'inline-flex', alignItems: 'center', gap: 6 },
  kvGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: sp[3],
  },
  kvK: { ...t.label, color: c.textFaint, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 },
  kvV: { ...t.bodySm, ...t.tabular },
  formTitle: { ...t.caption, color: c.textMuted, marginTop: sp[3], fontStyle: 'italic' },
  detailNote: {
    ...t.caption, color: c.textStrong, marginTop: sp[3], paddingTop: sp[3],
    borderTop: `1px solid ${c.border}`, lineHeight: 1.5,
  },
  code: { background: c.canvas, padding: '1px 4px', borderRadius: 3, fontFamily: t.familyMono },

  bench: { borderTop: `1px solid ${c.border}`, padding: `${sp[4]} ${sp[5]} ${sp[5]}` },
  benchTitle: { ...t.h3, color: c.text, marginBottom: sp[3] },
  benchRow: { display: 'flex', alignItems: 'center', gap: sp[3], marginBottom: sp[2] },
  benchLabel: { ...t.bodySm, color: c.textStrong, width: 190, flexShrink: 0 },
  benchTrack: { flex: 1, height: 14, background: c.canvas, borderRadius: 3, overflow: 'hidden' },
  benchFill: { height: '100%', borderRadius: 3 },
  benchVal: { ...t.bodySm, ...t.tabular, color: c.text, width: 76, textAlign: 'right', fontWeight: 600 },
  benchGap:  { height: 10 },
  benchNote: { ...t.caption, color: c.textMuted, marginTop: sp[3] },
};

function btnReset() {
  return { padding: 0, margin: 0, font: 'inherit', border: 'none', background: 'none', appearance: 'none' };
}
